"use client";

import {
  type InputHTMLAttributes,
  useMemo,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { mergeClasses } from "@huub-web/utils";

import type { ActiveProp, ClassNameProp, ValidatableProps } from "../../props";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { useFormValidation } from "../../hooks/useFormValidation";
import type { FieldMetadata } from "../../contexts/FormContext";
import { mergeRefs } from "../../utils";
import {
  RadioCardOption,
  type RadioCardOptionData,
} from "./components/RadioCardOption";
import { useWindowSize } from "../../hooks";

import { containerStyle, radioCardOptionStyle } from "./RadioCards.css";

export type { RadioCardOptionData as RadioCardOption };

const BREAKPOINT = 600;

export type RadioCardsChangeHandler = (value: string) => void;

export type RadioCardsProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "type"
> &
  ValidatableProps &
  ActiveProp &
  ClassNameProp & {
    /**
     * The current selected value
     */
    value?: string;
    /**
     * Array of radio card options
     */
    options: RadioCardOptionData[];
    /**
     * Whether to show success state
     */
    success?: boolean;
    /**
     * Whether to highlight the radio cards
     */
    highlight?: boolean;
    /**
     * Handler for value change
     */
    onChange?: RadioCardsChangeHandler;
    /**
     * The backend field key for this input (e.g. "applicant.housingType").
     * Passed by FormRow via cloneElement from the data-field-key attribute.
     * Forwarded to useFormValidation for AI agent field awareness.
     */
    fieldKey?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * RadioCards component for selecting a single option from a list using card-based UI.
 */
export const RadioCards: FC<RadioCardsProps> = ({
  id: idProp,
  disabled: disabledProp,
  error: errorProp,
  validation,
  name: nameProp,
  success,
  highlight,
  value,
  options,
  active,
  onChange,
  fieldKey,
  className,
  ref,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const internalRef = useRef<HTMLDivElement>(null);

  const windowSize = useWindowSize();

  const metadata = useMemo<FieldMetadata>(
    () => ({
      label: nameProp,
      type: "radio",
      options: options?.map((o) => ({ value: o.value, label: o.label })),
      required: validation?.includes("required"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nameProp, options, validation],
  );
  const { id, error, disabled } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name: nameProp,
    ref: inputRef,
    fieldKey,
    metadata,
  });

  const radioGroupName = nameProp || id || "radio-cards-group";

  const handleChange = (optionValue: string) => {
    if (!disabled) {
      onChange?.(optionValue);
    }
    scrollToIndex(options.findIndex((option) => option.value === optionValue));
  };

  const scrollToIndex = (index: number) => {
    if (wrapper === "carousel" && index >= 0) {
      const element = internalRef.current?.children[index];
      const container = internalRef.current;

      if (element && container) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollLeft =
          container.scrollLeft + (elementRect.left - containerRect.left);

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  };

  const [wrapper, setWrapper] = useState<"grid" | "carousel">("grid");

  useIsomorphicLayoutEffect(() => {
    const width = internalRef.current?.offsetWidth ?? 0;
    setWrapper(width > BREAKPOINT ? "grid" : "carousel");
  }, [options.length, windowSize]);

  useIsomorphicLayoutEffect(() => {
    if (wrapper === "carousel" && value) {
      const selectedIndex = options.findIndex(
        (option) => option.value === value,
      );
      if (selectedIndex >= 0) {
        scrollToIndex(selectedIndex);
      }
    }
  }, [wrapper]);

  return (
    <div
      id={id}
      ref={mergeRefs(internalRef, ref)}
      className={mergeClasses(
        containerStyle({
          wrapper,
          variant: options.length > 2 ? "default" : "twoColumn",
        }),
        className,
      )}
      role="radiogroup"
      data-wrapper={wrapper}
      {...(active && { "aria-active": true })}
    >
      {options.map((option, index) => {
        const optionID = `${id}-${index}`;
        const isChecked = value === option.value;
        const isDisabled = disabled || option.disabled;

        return (
          <RadioCardOption
            key={option.value}
            option={option}
            checked={isChecked}
            disabled={isDisabled}
            error={error}
            success={success}
            highlight={highlight}
            name={radioGroupName}
            optionID={optionID}
            onChange={handleChange}
            inputRef={index === 0 ? inputRef : undefined}
            className={radioCardOptionStyle({ wrapper })}
            {...props}
          />
        );
      })}
    </div>
  );
};
